import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  COMMON_RENDER_SIZES,
  COMMON_SOURCE_FORMATS,
  DEFAULT_SOURCE_FORMAT,
  formatRenderLabel,
  formatSourceLabel,
  makeSourceFormatKey,
} from "./constants.js";
import { host } from "./host.js";
import { createPreviewBridge, resolveCloudRendererUrl } from "./preview-bridge.js";
import { controlRoomWebSocketUrl, createDeviceRelay } from "./device-relay.js";
import {
  deleteFavorite,
  fetchFavorites,
  normalizeLens,
  putFavorite,
  queueLensSelection,
  readLastLensSelection,
  readRecentLenses,
  rememberLastLensSelection,
  rememberRecentLens,
  takeQueuedLensSelection,
} from "./lens-library.js";
import { isRuntimeConnectionError } from "./user-facing-error.js";
import { serializeError } from "./serialized-error.js";
import { selectDisplayedRenderTelemetry } from "./runtime-telemetry.js";
import {
  canAttemptVirtualCameraRestore,
  virtualCameraRestoreDelay,
  virtualCameraRuntimeSatisfied,
} from "./camera-restore.js";

const LensAppContext = createContext(null);
const MATCH_INPUT = "match-input";
const OUTPUT_FPS = [60, 30, 24];

const outputKey = (width, height, fps) => `${Math.round(width)}x${Math.round(height)}@${Math.round(fps)}`;

function parseOutputKey(value) {
  const match = String(value || "").match(/^(\d+)x(\d+)@(\d+)$/);
  return match
    ? { width: Number(match[1]), height: Number(match[2]), fps: Number(match[3]) }
    : { width: 1280, height: 720, fps: 60 };
}

function sourceFormat(settings) {
  return {
    width: Number(settings?.sourceWidth) || DEFAULT_SOURCE_FORMAT.width,
    height: Number(settings?.sourceHeight) || DEFAULT_SOURCE_FORMAT.height,
    fps: Number(settings?.sourceFps) || DEFAULT_SOURCE_FORMAT.fps,
  };
}

function outputFormat(settings, source) {
  if (settings?.outputMode === MATCH_INPUT) {
    const fallback = sourceFormat(settings);
    return {
      mode: MATCH_INPUT,
      width: Number(source?.width) || fallback.width,
      height: Number(source?.height) || fallback.height,
      fps: Number(source?.fps) || fallback.fps,
    };
  }
  return {
    mode: "fixed",
    width: Number(settings?.outputWidth) || 1280,
    height: Number(settings?.outputHeight) || 720,
    fps: Number(settings?.outputFps) || 60,
  };
}

function withTimeout(promise, timeoutMs, message) {
  let timer;
  return Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), timeoutMs);
    }),
  ]).finally(() => clearTimeout(timer));
}

function bytesFrom(payload) {
  const value = payload?.bytes ?? payload?.buffer;
  if (value instanceof ArrayBuffer) return value;
  if (ArrayBuffer.isView(value)) return value.buffer.slice(value.byteOffset, value.byteOffset + value.byteLength);
  return null;
}

async function browserLensFile() {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".lns,application/octet-stream";
    input.onchange = async () => {
      const file = input.files?.[0];
      resolve(file ? { lensId: file.name, fileName: file.name, source: file.name, bytes: await file.arrayBuffer() } : null);
    };
    input.click();
  });
}

export function useLensApp() {
  const value = useContext(LensAppContext);
  if (!value) throw new Error("useLensApp must be used inside <LensAppProvider>.");
  return value;
}

export function LensAppProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const settingsRef = useRef(null);
  settingsRef.current = settings;
  const [renderer, setRenderer] = useState({ ready: false, source: null, frameCount: 0, activeLenses: [] });
  const [runtimeStatus, setRuntimeStatus] = useState({ available: false, connected: false });
  const [status, setStatus] = useState("connecting cloud renderer");
  const [cameras, setCameras] = useState([]);
  const [cachedLenses, setCachedLenses] = useState([]);
  const [cachedLensInfo, setCachedLensInfo] = useState({});
  const [favorites, setFavorites] = useState([]);
  const [recentLenses, setRecentLenses] = useState(() => readRecentLenses());
  const [favoriteError, setFavoriteError] = useState("");
  const [busyLensId, setBusyLensId] = useState("");
  const [lastLens, setLastLens] = useState(() => readLastLensSelection());
  const [lensTransition, setLensTransition] = useState("");
  const [apiCalls, setApiCalls] = useState([]);
  const [sessionLogInfo, setSessionLogInfo] = useState({ available: false, path: "", maxBytes: 0 });
  const [logLines, setLogLines] = useState([]);
  const [lastError, setLastError] = useState(null);
  const [debugOverlayVisible, setDebugOverlayVisible] = useState(false);
  const [virtualCameraStatusText, setVirtualCameraStatusText] = useState("Not running");
  const [cameraTransition, setCameraTransition] = useState("");
  const [utilityPanel, setUtilityPanel] = useState("");
  const [renderFps, setRenderFps] = useState(0);
  const bridgeRef = useRef(null);
  const lastLensRef = useRef(lastLens);
  const relayRef = useRef(null);
  const frameRef = useRef(null);
  const frameSampleRef = useRef({ count: 0, at: performance.now() });
  const previewShellRef = useRef(null);
  const smokeMode = useMemo(() => new URLSearchParams(window.location.search).has("smoke"), []);
  const libraryMode = useMemo(() => window.location.pathname.replace(/\/+$/, "") === "/ui/lenses", []);

  const rendererBase = useMemo(() => resolveCloudRendererUrl(
    window.location,
    import.meta.env.VITE_LENSCORE_RENDERER_URL || "",
  ), []);
  const rendererOrigin = useMemo(() => new URL(rendererBase).origin, [rendererBase]);
  const previewUrl = useMemo(() => {
    const url = new URL(rendererBase);
    url.searchParams.set("chrome", "hidden");
    // The embedded renderer is command-driven. Without this guard it opens
    // the default camera as soon as the iframe mounts, which can recursively
    // activate LensCore's own virtual camera before mode restoration runs.
    url.searchParams.set("headless", "1");
    if (debugOverlayVisible) url.searchParams.set("debug", "1");
    return url.toString();
  }, [debugOverlayVisible, rendererBase]);

  const appendLog = useCallback((message, detail) => {
    const stamp = new Date().toLocaleTimeString();
    const line = detail === undefined ? `${stamp} ${message}` : `${stamp} ${message} ${JSON.stringify(detail)}`;
    setLogLines((old) => [line, ...old].slice(0, 120));
    try { host.writeLog("info", message, detail); } catch {
      // Logging must never disrupt camera or lens control.
    }
  }, []);

  const setError = useCallback((error) => {
    const next = serializeError(error);
    setLastError(next);
    setStatus(`error ${next.message}`);
    appendLog("error", next);
  }, [appendLog]);

  useEffect(() => {
    const bridge = createPreviewBridge({ targetOrigin: rendererOrigin });
    bridgeRef.current = bridge;
    const unsubscribe = bridge.subscribe((message) => {
      if (message.type === "ready") {
        setStatus("running");
        setRenderer((old) => ({ ...old, ready: true }));
      } else if (message.type === "state") {
        setRenderer((old) => ({ ...old, ...message }));
        if (message.ready) setStatus("running");
        const now = performance.now();
        const count = Number(message.frameCount) || 0;
        const sample = frameSampleRef.current;
        if (now - sample.at >= 500) {
          setRenderFps(Math.max(0, ((count - sample.count) * 1000) / (now - sample.at)));
          frameSampleRef.current = { count, at: now };
        }
      } else if (message.type === "log") {
        appendLog(`renderer ${message.level}: ${message.message}`, message.detail);
      }
    });
    if (frameRef.current?.contentWindow) bridge.setTarget(frameRef.current.contentWindow);
    return () => { unsubscribe(); bridge.destroy(); bridgeRef.current = null; };
  }, [appendLog, rendererOrigin]);

  const attachPreviewFrame = useCallback((element) => {
    frameRef.current = element;
    bridgeRef.current?.setTarget(element?.contentWindow || null);
  }, []);

  const native = useCallback(async (type, payload = {}, required = false) => {
    if (!runtimeStatus.available && !required) {
      return null;
    }
    try {
      const response = await host.runtimeCommand(type, payload);
      if (response?.status) setRuntimeStatus((old) => ({ ...old, available: true, status: response.status }));
      return response;
    } catch (error) {
      appendLog(`native ${type} failed`, { message: error?.message });
      if (required) throw error;
      return null;
    }
  }, [appendLog, runtimeStatus.available, runtimeStatus.error]);

  const preview = useCallback((command, payload) => {
    if (!bridgeRef.current) return Promise.reject(new Error("Cloud renderer is not attached."));
    return bridgeRef.current.command(command, payload);
  }, []);

  const enumerateCameras = useCallback(async () => {
    const devices = await navigator.mediaDevices?.enumerateDevices?.() || [];
    let nativeCameras = [];
    try {
      const response = await host.runtimeCommand("LIST_CAMERAS", {});
      nativeCameras = Array.isArray(response?.cameras) ? response.cameras : [];
    } catch (error) {
      appendLog("native camera mode enumeration unavailable", { message: error?.message });
    }
    const next = devices.filter((device) => device.kind === "videoinput").map((device, index) => ({
      deviceId: device.deviceId,
      label: device.label || `Camera ${index + 1}`,
      groupId: device.groupId,
      modes: (() => {
        const nativeModes = nativeCameras.find((camera) => {
          const browserLabel = String(device.label || "").trim().toLowerCase();
          const nativeLabel = String(camera?.label || "").trim().toLowerCase();
          return browserLabel && nativeLabel
            && (browserLabel === nativeLabel || browserLabel.startsWith(`${nativeLabel} (`));
        })?.modes || [];
        const unique = new Map();
        for (const mode of nativeModes) {
          const normalized = {
            ...mode,
            width: Math.round(Number(mode.width) || 0),
            height: Math.round(Number(mode.height) || 0),
            fps: Math.round(Number(mode.fps) || 0),
          };
          if (!normalized.width || !normalized.height || !normalized.fps) continue;
          const key = makeSourceFormatKey(normalized.width, normalized.height, normalized.fps);
          const previous = unique.get(key);
          unique.set(key, {
            ...normalized,
            subtypes: [...new Set([...(previous?.subtypes || []), ...(normalized.subtypes || [])])],
          });
        }
        return [...unique.values()];
      })(),
    }));
    setCameras(next);
    host.reportCameras({ cameras: next });
    return next;
  }, [appendLog]);

  const startPreviewCamera = useCallback(async (deviceId = settingsRef.current?.cameraDeviceId || "") => {
    const savedLabel = String(settingsRef.current?.cameraLabel || "").trim();
    const selected = cameras.find((camera) => camera.deviceId === deviceId)
      || (!deviceId && savedLabel
        ? cameras.find((camera) => camera.label === savedLabel)
        : null);
    const resolvedDeviceId = selected?.deviceId || deviceId;
    const label = selected?.label || savedLabel;
    const preferredFormat = sourceFormat(settingsRef.current);
    const updated = await host.updateSettings({ cameraDeviceId: resolvedDeviceId, cameraLabel: label });
    settingsRef.current = updated;
    setSettings(updated);
    await preview("setDeviceClass", { deviceClass: updated.rendererDeviceClass });
    const result = await preview(label ? "startCameraByLabel" : "startCamera", label
      ? { label, preferredFormat }
      : { deviceId: resolvedDeviceId, preferredFormat });
    if (label) await native("SET_CAMERA", { label, ...preferredFormat });
    return result;
  }, [cameras, native, preview]);

  const stopPreviewCamera = useCallback(async () => {
    if (!renderer.ready) return { stopped: true, skipped: true };
    return preview("stopCamera", {});
  }, [preview, renderer.ready]);

  const setCameraMode = useCallback(async (mode, options = {}) => {
    if (!["off", "preview", "virtual-camera"].includes(mode)) {
      throw new Error(`Unknown camera mode '${mode}'.`);
    }
    setLastError(null);
    setCameraTransition(mode);
    const requestedDeviceId = options.deviceId ?? settingsRef.current?.cameraDeviceId ?? "";
    const requestedLabel = String(options.label ?? settingsRef.current?.cameraLabel ?? "").trim();
    const selected = cameras.find((camera) => camera.deviceId === requestedDeviceId)
      || (!requestedDeviceId && requestedLabel
        ? cameras.find((camera) => camera.label === requestedLabel)
        : null);
    const selectedDeviceId = selected?.deviceId || requestedDeviceId;
    const selectedLabel = selected?.label || requestedLabel;
    const preferredFormat = sourceFormat(settingsRef.current);
    try {
      if (mode === "off") {
        const updated = await host.updateSettings({
          cameraMode: "off",
          cameraAutoStart: false,
          virtualCameraEnabled: false,
        });
        settingsRef.current = updated;
        setSettings(updated);
        await stopPreviewCamera();
        await withTimeout(
          native("STOP_CEF", {}),
          5000,
          "Timed out asking the native renderer to release the camera.",
        ).catch((error) => appendLog("native camera release continued in background", { message: error.message }));
        void withTimeout(
          native("STOP_VCAM", {}),
          5000,
          "Timed out stopping virtual-camera output.",
        ).catch((error) => appendLog("virtual-camera stop continued in background", { message: error.message }));
        setVirtualCameraStatusText("Off — camera released");
        return { mode, settings: updated };
      }

      if (mode === "preview") {
        const transitionStartedAt = performance.now();
        // Commit camera ownership before touching the native runtime. A dead
        // bridge must not leave the UI permanently claiming Virtual mode.
        const updated = await host.updateSettings({
          cameraMode: "preview",
          cameraAutoStart: true,
          virtualCameraEnabled: false,
          cameraDeviceId: selectedDeviceId,
          cameraLabel: selectedLabel,
        });
        settingsRef.current = updated;
        setSettings(updated);
        setVirtualCameraStatusText("Releasing the virtual renderer…");
        await withTimeout(
          native("STOP_CEF", {}),
          5000,
          "Timed out asking the native renderer to release the camera.",
        ).catch((error) => appendLog("preview handoff continued without native acknowledgement", { message: error.message }));
        const stopVirtualOutput = withTimeout(
          native("STOP_VCAM", {}),
          5000,
          "Timed out stopping virtual-camera output.",
        );
        const result = selectedLabel
          ? await preview("startCameraByLabel", { label: selectedLabel, preferredFormat })
          : await preview("startCamera", { deviceId: selectedDeviceId, preferredFormat });
        void stopVirtualOutput.then(
          () => appendLog("virtual output stopped after preview handoff", {
            elapsedMs: Math.round(performance.now() - transitionStartedAt),
          }),
          (error) => appendLog("preview active while virtual output finishes stopping", { message: error.message }),
        );
        setVirtualCameraStatusText("Preview owns the camera");
        return { mode, result, settings: updated };
      }

      await stopPreviewCamera();
      const output = outputFormat(settingsRef.current, renderer.source);
      try {
        await native("SET_CONFIG", {
          width: output.width,
          height: output.height,
          fps: output.fps,
          vcamName: settingsRef.current?.virtualCameraName,
        }, true);
        await native("SET_CAMERA", {
          deviceId: selectedDeviceId,
          label: selectedLabel,
          ...preferredFormat,
        }, true);
        await native("START_CEF", {
          url: rendererBase,
          width: output.width,
          height: output.height,
          fps: output.fps,
        }, true);
        await native("START_VCAM", {}, true);
      } catch (error) {
        await native("STOP_CEF", {});
        await native("STOP_VCAM", {});
        const preserveVirtualIntent = options.restoring === true && isRuntimeConnectionError(error);
        if (!preserveVirtualIntent) {
          const fallback = await host.updateSettings({
            cameraMode: "off",
            cameraAutoStart: false,
            virtualCameraEnabled: false,
          });
          settingsRef.current = fallback;
          setSettings(fallback);
        }
        throw error;
      }
      const updated = await host.updateSettings({
        cameraMode: "virtual-camera",
        cameraAutoStart: false,
        virtualCameraEnabled: true,
        cameraDeviceId: selectedDeviceId,
        cameraLabel: selectedLabel,
      });
      settingsRef.current = updated;
      setSettings(updated);
      setVirtualCameraStatusText("Virtual camera owns the camera");
      return { mode, settings: updated };
    } finally {
      setCameraTransition("");
    }
  }, [appendLog, cameras, native, preview, renderer.ready, renderer.source, rendererBase, runtimeStatus.platform, stopPreviewCamera]);

  const startCamera = useCallback((deviceId = settingsRef.current?.cameraDeviceId || "") => (
    setCameraMode("preview", { deviceId })
  ), [setCameraMode]);

  const startCameraByLabel = useCallback((label) => (
    setCameraMode("preview", { deviceId: "", label: String(label || "").trim() })
  ), [setCameraMode]);

  const stopCamera = useCallback(() => setCameraMode("off"), [setCameraMode]);

  const selectCamera = useCallback(async (deviceId) => {
    const selected = cameras.find((camera) => camera.deviceId === deviceId);
    const label = deviceId ? (selected?.label || "") : "";
    const updated = await host.updateSettings({ cameraDeviceId: deviceId, cameraLabel: label });
    settingsRef.current = updated;
    setSettings(updated);
    if (updated.cameraMode === "preview") return startPreviewCamera(deviceId);
    if (updated.cameraMode === "virtual-camera") {
      return native("SET_CAMERA", { deviceId, label, ...sourceFormat(updated) }, true);
    }
    return { mode: "off", settings: updated };
  }, [cameras, native, startPreviewCamera]);

  const applyOutputResolution = useCallback(async (selection) => {
    const parsed = selection === MATCH_INPUT ? { mode: MATCH_INPUT } : { mode: "fixed", ...parseOutputKey(selection) };
    const patch = parsed.mode === MATCH_INPUT ? { outputMode: MATCH_INPUT } : {
      outputMode: "fixed", outputWidth: parsed.width, outputHeight: parsed.height, outputFps: parsed.fps,
    };
    const updated = await host.updateSettings(patch);
    setSettings(updated);
    const output = outputFormat(updated, renderer.source);
    await preview("setOutputSize", { width: output.width, height: output.height });
    await preview("setFPSLimit", { fps: output.fps });
    await native("SET_CONFIG", { width: output.width, height: output.height, fps: output.fps });
    return output;
  }, [native, preview, renderer.source]);

  const applySourceFormat = useCallback(async (key) => {
    const format = parseOutputKey(key);
    const updated = await host.updateSettings({
      sourceWidth: format.width, sourceHeight: format.height, sourceFps: format.fps,
    });
    settingsRef.current = updated;
    setSettings(updated);
    if (updated.cameraMode === "preview" && renderer.source) await startPreviewCamera(updated.cameraDeviceId || "");
    if (updated.cameraMode === "virtual-camera") {
      await native("SET_CAMERA", {
        deviceId: updated.cameraDeviceId || "",
        label: updated.cameraLabel || "",
        ...sourceFormat(updated),
      }, true);
    }
    return format;
  }, [native, renderer.source, startPreviewCamera]);

  const refreshCachedLenses = useCallback(async () => {
    const cache = await host.getCachedLenses();
    setCachedLenses(Array.isArray(cache?.lenses) ? cache.lenses : []);
    setCachedLensInfo({ manifestPath: cache?.manifestPath || "", directory: cache?.directory || "", error: cache?.error || null });
    return cache;
  }, []);

  const loadLensUrl = useCallback(async (url, replace, lensId = "", lensName = "") => {
    const value = String(url || "").trim();
    const id = String(lensId || "").trim();
    if (!value && !/^\d{1,20}$/.test(id)) throw new Error("A lens ID or URL is required.");
    const payload = await host.fetchLensUrl({ url: value || undefined, lensId: id || undefined });
    const bytes = bytesFrom(payload);
    if (!bytes) throw new Error("Lens download did not contain bytes.");
    const command = replace ? "loadLens" : "addLens";
    const nativeCommand = replace ? "LOAD_LENS_URL" : "ADD_LENS_URL";
    const nativeIsLive = settingsRef.current?.cameraMode === "virtual-camera";

    // The native process remains alive when CEF is not rendering. Keep its
    // lens list synchronized so switching to Virtual does not lose the lens.
    // In Virtual mode it is the live output, so a native failure must abort
    // the operation instead of reporting a preview-only success.
    await native(nativeCommand, {
      lensId: id || payload.lensId || undefined,
      ...(!id && value ? { url: value } : {}),
    }, nativeIsLive);
    const result = await preview(command, {
      url: bytes,
      options: {
        lensId: id || payload.lensId || undefined,
        source: id ? `lens:${id}` : value,
      },
    });
    const remembered = rememberLastLensSelection({
      url: id ? "" : value,
      lensId: id || payload.lensId || "",
      name: lensName || lensId || payload.lensId || "Last lens",
    });
    lastLensRef.current = remembered;
    setLastLens(remembered);
    return result;
  }, [native, preview]);

  const refreshFavorites = useCallback(async () => {
    try {
      const next = await fetchFavorites();
      setFavorites(next);
      setFavoriteError("");
      return next;
    } catch (error) {
      setFavoriteError(error?.message || String(error));
      return [];
    }
  }, []);

  const toggleFavorite = useCallback(async (lens) => {
    const normalized = normalizeLens(lens);
    const existed = favorites.some((item) => item.lensId === normalized.lensId);
    const previous = favorites;
    setFavoriteError("");
    setFavorites((current) => existed
      ? current.filter((item) => item.lensId !== normalized.lensId)
      : [normalized, ...current.filter((item) => item.lensId !== normalized.lensId)]);
    try {
      if (existed) await deleteFavorite(normalized.lensId);
      else await putFavorite(normalized);
    } catch (error) {
      setFavorites(previous);
      setFavoriteError(error?.message || String(error));
    }
  }, [favorites]);

  const applyCatalogLens = useCallback(async (lens) => {
    const normalized = normalizeLens(lens);
    setBusyLensId(normalized.lensId);
    setRecentLenses(rememberRecentLens(normalized));
    try {
      return await loadLensUrl("", true, normalized.lensId, normalized.name);
    } finally {
      setBusyLensId("");
    }
  }, [loadLensUrl]);

  const selectCatalogLens = useCallback(async (lens) => {
    const normalized = normalizeLens(lens);
    if (libraryMode) {
      queueLensSelection(normalized);
      window.location.assign("/ui/");
      return { queued: true, lensId: normalized.lensId };
    }
    return applyCatalogLens(normalized);
  }, [applyCatalogLens, libraryMode]);

  const loadLocalLens = useCallback(async (payload, replace) => {
    const bytes = bytesFrom(payload);
    if (!bytes) throw new Error("Lens payload does not contain bytes.");
    appendLog("Local lens applied to preview only; use a cloud URL to update every renderer.");
    const result = await preview(replace ? "applyLens" : "addLens", {
      url: bytes,
      options: { lensId: payload.lensId || payload.fileName, source: payload.source || payload.fileName },
    });
    const remembered = {
      kind: "buffer",
      lensId: String(payload.lensId || payload.fileName || "local-lens"),
      name: String(payload.fileName || payload.lensId || "Local lens"),
      source: String(payload.source || payload.fileName || "local file"),
      bytes,
    };
    lastLensRef.current = remembered;
    setLastLens({ ...remembered, bytes: undefined });
    return result;
  }, [appendLog, preview]);

  const loadCachedLens = useCallback(async (lensId, replace) => {
    const entry = cachedLenses.find((lens) => lens.lensId === lensId || lens.fileName === lensId);
    const url = entry?.metadata?.lens_url || entry?.metadata?.lensUrl || "";
    if (url) return loadLensUrl(url, replace, entry.lensId);
    return loadLocalLens(await host.cachedLensFile({ lensId }), replace);
  }, [cachedLenses, loadLensUrl, loadLocalLens]);

  const loadLensFromFile = useCallback(async (fileOrPayload, replace) => {
    const payload = fileOrPayload instanceof File
      ? { lensId: fileOrPayload.name, fileName: fileOrPayload.name, source: fileOrPayload.name, bytes: await fileOrPayload.arrayBuffer() }
      : fileOrPayload;
    return loadLocalLens(payload, replace);
  }, [loadLocalLens]);

  const loadLensFromSelector = useCallback(async () => {
    let payload = null;
    try { payload = await host.selectLensFile(); } catch { /* web UI uses its own picker */ }
    if (!payload) payload = await browserLensFile();
    return payload ? loadLocalLens(payload, true) : null;
  }, [loadLocalLens]);

  const removeLens = useCallback(async (lensId) => {
    const lens = (renderer.activeLenses || []).find((item) => item.lensId === lensId);
    await native("REMOVE_LENS", {
      id: lensId,
      lensId,
      ...(typeof lens?.source === "string" && /^https?:/i.test(lens.source) ? { url: lens.source } : {}),
    }, settingsRef.current?.cameraMode === "virtual-camera");
    const result = await preview("removeLens", { lensId });
    return result;
  }, [native, preview, renderer.activeLenses]);
  const clearLenses = useCallback(async () => {
    await native("CLEAR_LENSES", {}, settingsRef.current?.cameraMode === "virtual-camera");
    const result = await preview("clearLenses", {});
    return result;
  }, [native, preview]);

  const setLensEnabled = useCallback(async (enabled) => {
    setLensTransition(enabled ? "on" : "off");
    try {
      if (!enabled) {
        await clearLenses();
        return { enabled: false };
      }
      if ((renderer.activeLenses || []).length) return { enabled: true, alreadyActive: true };
      const remembered = lastLensRef.current;
      if (!remembered) throw new Error("Load a lens before turning the lens back on.");
      if (remembered.kind === "id") {
        await loadLensUrl("", true, remembered.lensId, remembered.name);
      } else if (remembered.kind === "url") {
        await loadLensUrl(remembered.url, true, remembered.lensId, remembered.name);
      } else if (remembered.kind === "buffer" && remembered.bytes) {
        await loadLocalLens(remembered, true);
      } else {
        throw new Error("The last local lens is no longer available. Load it again first.");
      }
      return { enabled: true, lensId: remembered.lensId || "" };
    } finally {
      setLensTransition("");
    }
  }, [clearLenses, loadLensUrl, loadLocalLens, renderer.activeLenses]);

  const toggleCatalogLens = useCallback(async (lens) => {
    const normalized = normalizeLens(lens);
    const active = (renderer.activeLenses || []).some((item) => String(item.lensId || "") === normalized.lensId);
    return active ? setLensEnabled(false) : selectCatalogLens(normalized);
  }, [renderer.activeLenses, selectCatalogLens, setLensEnabled]);

  const setDebugOverlay = useCallback(async (visible) => {
    setDebugOverlayVisible(Boolean(visible));
    return { visible: Boolean(visible) };
  }, []);
  const openUtilityPanel = useCallback(async (panel) => {
    const next = panel === "api" ? "api" : "diagnostics";
    if (next === "api") setApiCalls(await host.getApiCalls());
    setUtilityPanel(next);
    return { panel: next };
  }, []);
  const closeUtilityPanel = useCallback(() => setUtilityPanel(""), []);
  const openSessionLog = useCallback(() => {
    if (typeof host.openSessionLog !== "function") {
      return Promise.reject(new Error("Update LensCore Control to enable session logs."));
    }
    return host.openSessionLog();
  }, []);
  const saveApiSettings = useCallback(async (patch) => {
    const next = await host.updateSettings(patch); setSettings(next); return next;
  }, []);
  const copyApiToken = useCallback(() => host.copyApiToken(), []);
  const rotateApiToken = useCallback(() => host.rotateApiToken(), []);
  const saveRemoteControlSettings = useCallback(async (patch) => {
    const next = await host.updateSettings(patch); setSettings(next); return next;
  }, []);
  const saveVirtualCameraSettings = useCallback(async (patch) => {
    const next = await host.updateSettings(patch);
    setSettings(next);
    await native("SET_CONFIG", {
      width: next.virtualCameraWidth, height: next.virtualCameraHeight,
      fps: next.virtualCameraFps, vcamName: next.virtualCameraName,
    });
    return next;
  }, [native]);
  const saveRendererSettings = useCallback(async (patch) => {
    const previousDeviceClass = settingsRef.current?.rendererDeviceClass;
    const next = await host.updateSettings(patch);
    settingsRef.current = next;
    setSettings(next);
    if (next.rendererDeviceClass === previousDeviceClass) return next;

    if (renderer.ready) {
      await preview("setDeviceClass", { deviceClass: next.rendererDeviceClass });
    }
    if (next.cameraMode === "virtual-camera") {
      const format = outputFormat(next, renderer.source);
      await native("STOP_CEF", {}, true);
      await native("START_CEF", {
        width: format.width,
        height: format.height,
        fps: format.fps,
      }, true);
    }
    appendLog("renderer device class changed", {
      previous: previousDeviceClass || "undefined",
      current: next.rendererDeviceClass,
      nativeRendererRestarted: next.cameraMode === "virtual-camera",
    });
    return next;
  }, [appendLog, native, preview, renderer.ready, renderer.source]);
  const runVirtualCamera = useCallback(async (command, patch) => {
    if (patch) await saveVirtualCameraSettings(patch);
    const type = { start: "START_VCAM", stop: "STOP_VCAM", remove: "REMOVE_VCAM", shutdown: "SHUTDOWN_VCAM" }[command];
    if (!type) throw new Error(`Unknown virtual camera command '${command}'.`);
    const result = await native(type, {}, true);
    setVirtualCameraStatusText(`${command} ok`);
    appendLog(`virtual camera ${command}`, result);
    return result;
  }, [appendLog, native, saveVirtualCameraSettings]);
  const toggleSettingsCollapsed = useCallback(async () => {
    const next = await host.updateSettings({ settingsCollapsed: !settingsRef.current?.settingsCollapsed });
    setSettings(next);
  }, []);

  const executeCommand = useCallback(async (command, payload = {}) => {
    switch (command) {
      case "get-state": return {
        ...renderer,
        status,
        cameraMode: settingsRef.current?.cameraMode || "off",
        lastError,
        runtime: runtimeStatus,
      };
      case "set-camera-mode": case "setCameraMode": return setCameraMode(payload.mode || "off", payload);
      case "start-camera": return startCamera(payload.deviceId || "");
      case "start-camera-by-label": {
        return startCameraByLabel(payload.label);
      }
      case "startCamera": return startCamera(payload.deviceId || "");
      case "startCameraByLabel": return startCameraByLabel(payload.label);
      case "select-camera": case "selectCamera": return selectCamera(payload.deviceId || "");
      case "stop-camera": return stopCamera();
      case "stopCamera": return stopCamera();
      case "set-output-resolution": return applyOutputResolution(payload.outputMode === MATCH_INPUT
        ? MATCH_INPUT : outputKey(payload.outputWidth, payload.outputHeight, payload.outputFps));
      case "set-source-format": return applySourceFormat(outputKey(payload.sourceWidth, payload.sourceHeight, payload.sourceFps));
      case "setOutputSize": return applyOutputResolution(outputKey(
        payload.width,
        payload.height,
        settingsRef.current?.outputFps || 60,
      ));
      case "setFPSLimit": return applyOutputResolution(outputKey(
        settingsRef.current?.outputWidth || 1280,
        settingsRef.current?.outputHeight || 720,
        payload.fps,
      ));
      case "applyLens": return loadLensUrl(payload.url ?? payload, true, payload.options?.lensId);
      case "addLens": return loadLensUrl(payload.url ?? payload, false, payload.options?.lensId);
      case "load-lens-url": case "loadLensUrl": return loadLensUrl(payload.url, true, payload.lensId);
      case "add-lens-url": case "addLensUrl": return loadLensUrl(payload.url, false, payload.lensId);
      case "load-lens-id": return loadLensUrl("", true, payload.lensId, payload.name);
      case "add-lens-id": return loadLensUrl("", false, payload.lensId, payload.name);
      case "load-lens-buffer": case "load-lens-file": case "loadLensFile": return loadLocalLens(payload, true);
      case "add-lens-buffer": case "add-lens-file": case "addLensFile": return loadLocalLens(payload, false);
      case "clear-lenses": case "clearLenses": case "clearLens": case "unloadLens": return clearLenses();
      case "enable-lens": case "lensOn": return setLensEnabled(true);
      case "disable-lens": case "lensOff": return setLensEnabled(false);
      case "remove-lens": case "removeLens": return removeLens(payload.lensId);
      case "set-debug-overlay": return setDebugOverlay(payload.visible);
      case "trigger-effect": case "triggerEffect": {
        const result = await preview("triggerEffect", payload);
        await native("TRIGGER_EFFECT", payload);
        return result;
      }
      default: throw new Error(`Unknown command: ${command}`);
    }
  }, [
    applyOutputResolution, applySourceFormat, cameras, clearLenses, lastError, loadLensUrl,
    loadLocalLens, native, preview, removeLens, renderer, runtimeStatus, setDebugOverlay,
    selectCamera, setCameraMode, setLensEnabled, startCamera, startCameraByLabel, status, stopCamera,
  ]);
  const executeCommandRef = useRef(executeCommand);
  executeCommandRef.current = executeCommand;

  useEffect(() => {
    if (!renderer.ready || !settings?.remoteControlEnabled || !settings.remoteControlDeviceId) return undefined;
    const participantId = `${settings.remoteControlDeviceId}:runtime`;
    const relay = createDeviceRelay({
      url: controlRoomWebSocketUrl({ room: settings.remoteControlRoom || "default", baseUrl: rendererBase }),
      id: participantId,
      onCommand: (command, payload) => executeCommandRef.current(command, payload),
      onStatus: (next) => appendLog(`remote control ${next}`, { participantId }),
      onError: () => appendLog("remote control websocket error"),
    });
    relayRef.current = relay;
    relay.publishState({ ready: renderer.ready });
    return () => {
      relay.destroy();
      if (relayRef.current === relay) relayRef.current = null;
    };
  }, [
    appendLog, renderer.ready, rendererBase,
    settings?.remoteControlDeviceId, settings?.remoteControlEnabled, settings?.remoteControlRoom,
  ]);

  useEffect(() => {
    let disposed = false;
    let unsubscribeCommand = () => {};
    let unsubscribeSettings = () => {};
    let unsubscribeRuntime = () => {};
    let unsubscribeMenu = () => {};
    (async () => {
      try {
        const [nextSettings, calls, runtime, nextSessionLogInfo] = await Promise.all([
          host.getSettings(), host.getApiCalls(), host.runtimeStatus(),
          typeof host.getSessionLogInfo === "function"
            ? host.getSessionLogInfo()
            : Promise.resolve({ available: false, path: "", maxBytes: 0 }),
        ]);
        if (disposed) return;
        setSettings(nextSettings);
        setApiCalls(Array.isArray(calls) ? calls : []);
        setRuntimeStatus(runtime || { available: false, connected: false });
        setSessionLogInfo(nextSessionLogInfo || { available: false, path: "", maxBytes: 0 });
        await Promise.all([refreshCachedLenses(), refreshFavorites()]);
        await enumerateCameras();
        unsubscribeCommand = host.onCommand(async ({ requestId, command, payload }) => {
          try { host.commandResult({ requestId, ok: true, result: await executeCommandRef.current(command, payload || {}) }); }
          catch (error) {
            setError(error);
            host.commandResult({ requestId, ok: false, error: serializeError(error) });
          }
        });
        unsubscribeSettings = host.onSettingsChanged(setSettings);
        unsubscribeRuntime = host.onRuntimeStatus((nextRuntime) => {
          setRuntimeStatus(nextRuntime);
          if (nextRuntime?.available && nextRuntime?.connected) {
            setLastError((previousError) => (
              isRuntimeConnectionError(previousError) ? null : previousError
            ));
          }
        });
        unsubscribeMenu = host.onMenuAction((message) => {
          if (message?.action === "open-panel") openUtilityPanel(message.panel).catch(setError);
        });
        host.ready();
      } catch (error) { setError(error); host.ready(); }
    })();
    const deviceChange = () => enumerateCameras().catch(setError);
    navigator.mediaDevices?.addEventListener?.("devicechange", deviceChange);
    return () => {
      disposed = true;
      unsubscribeCommand(); unsubscribeSettings(); unsubscribeRuntime(); unsubscribeMenu();
      navigator.mediaDevices?.removeEventListener?.("devicechange", deviceChange);
    };
  }, [enumerateCameras, openUtilityPanel, refreshCachedLenses, refreshFavorites, setError]);

  const autoStart = useRef(false);
  const virtualRestoreInFlight = useRef(false);
  const virtualRestoreAttempt = useRef(0);
  const virtualRestoreTimer = useRef(null);
  const [virtualRestoreRetry, setVirtualRestoreRetry] = useState(0);
  useEffect(() => {
    if (!renderer.ready || !settings || smokeMode || libraryMode) return;
    if (settings.cameraMode === "preview") {
      if (autoStart.current) return;
      autoStart.current = true;
      startPreviewCamera(settings.cameraDeviceId || "").catch(setError);
    } else if (settings.cameraMode === "virtual-camera") {
      const runtimeSatisfiesDesiredMode = virtualCameraRuntimeSatisfied(runtimeStatus);
      if (runtimeSatisfiesDesiredMode) {
        autoStart.current = true;
        virtualRestoreAttempt.current = 0;
        if (virtualRestoreTimer.current) {
          clearTimeout(virtualRestoreTimer.current);
          virtualRestoreTimer.current = null;
        }
        return;
      }
      if (!canAttemptVirtualCameraRestore({
        rendererReady: renderer.ready,
        runtimeAvailable: runtimeStatus.available,
        runtimeConnected: runtimeStatus.connected,
        runtimeSatisfiesDesiredMode,
        inFlight: virtualRestoreInFlight.current,
        retryScheduled: Boolean(virtualRestoreTimer.current),
      })) return;
      virtualRestoreInFlight.current = true;
      setCameraMode("virtual-camera", { restoring: true }).then(() => {
        const attempts = virtualRestoreAttempt.current + 1;
        autoStart.current = true;
        virtualRestoreAttempt.current = 0;
        setLastError((previousError) => (
          isRuntimeConnectionError(previousError) ? null : previousError
        ));
        setStatus("running");
        appendLog("virtual camera desired state restored", {
          attempts,
        });
      }).catch((error) => {
        if (!isRuntimeConnectionError(error)) {
          autoStart.current = true;
          setError(error);
          return;
        }
        const attempt = virtualRestoreAttempt.current + 1;
        virtualRestoreAttempt.current = attempt;
        const delayMs = virtualCameraRestoreDelay(attempt);
        setError(error);
        appendLog("virtual camera reconciliation will retry", { attempt, delayMs });
        virtualRestoreTimer.current = setTimeout(() => {
          virtualRestoreTimer.current = null;
          setVirtualRestoreRetry((value) => value + 1);
        }, delayMs);
      }).finally(() => {
        virtualRestoreInFlight.current = false;
      });
    } else {
      if (virtualRestoreTimer.current) {
        clearTimeout(virtualRestoreTimer.current);
        virtualRestoreTimer.current = null;
      }
      virtualRestoreAttempt.current = 0;
      if (autoStart.current) return;
      autoStart.current = true;
      stopPreviewCamera().catch(setError);
    }
  }, [
    appendLog, libraryMode, renderer.ready, runtimeStatus, runtimeStatus.available, runtimeStatus.connected,
    setCameraMode, settings, smokeMode, startPreviewCamera, stopPreviewCamera, setError,
    virtualRestoreRetry,
  ]);

  useEffect(() => () => {
    if (virtualRestoreTimer.current) clearTimeout(virtualRestoreTimer.current);
  }, []);

  const queuedLensApplied = useRef(false);
  useEffect(() => {
    if (!renderer.ready || libraryMode || queuedLensApplied.current) return;
    queuedLensApplied.current = true;
    const queued = takeQueuedLensSelection();
    if (queued) applyCatalogLens(queued).catch(setError);
  }, [applyCatalogLens, libraryMode, renderer.ready, setError]);

  useEffect(() => {
    host.reportState({
      status, frameCount: renderer.frameCount, source: renderer.source,
      measuredFps: renderer.measuredFps ?? renderFps,
      renderTarget: outputFormat(settings, renderer.source),
      activeLenses: renderer.activeLenses || [], lastError,
    });
  }, [lastError, renderFps, renderer, settings, status]);

  useEffect(() => {
    relayRef.current?.publishState({
      ready: renderer.ready,
      source: renderer.source,
      frameCount: renderer.frameCount,
      activeLenses: renderer.activeLenses || [],
      error: renderer.error || lastError?.message || null,
      lensError: renderer.lensError || (lastError?.code ? lastError : null),
      runtime: runtimeStatus,
    });
  }, [lastError, renderer, runtimeStatus]);

  const input = sourceFormat(settings);
  const output = outputFormat(settings, renderer.source);
  const displayedRenderTelemetry = useMemo(() => selectDisplayedRenderTelemetry({
    cameraMode: settings?.cameraMode,
    previewFps: renderFps,
    previewFrameCount: renderer.frameCount,
    runtimeStatus,
  }), [renderFps, renderer.frameCount, runtimeStatus, settings?.cameraMode]);
  const nativeFailureMessage = runtimeStatus.status?.lastError
    || (settings && runtimeStatus.available === false ? runtimeStatus.error : "")
    || "";
  const effectiveLastError = lastError || renderer.lensError || (nativeFailureMessage ? { message: nativeFailureMessage } : null);
  const selectedCamera = cameras.find((camera) => camera.deviceId === settings?.cameraDeviceId)
    || cameras.find((camera) => camera.label === settings?.cameraLabel);
  const sourceFormats = selectedCamera?.modes?.length ? selectedCamera.modes : COMMON_SOURCE_FORMATS;
  const sourceFormatOptions = useMemo(() => sourceFormats.map((format) => ({
    ...format, key: makeSourceFormatKey(format.width, format.height, format.fps),
    label: `${formatSourceLabel(format.width, format.height, format.fps)}${format.subtypes?.length ? ` · ${format.subtypes.join("/")}` : ""}`,
  })), [sourceFormats]);
  const outputOptions = useMemo(() => COMMON_RENDER_SIZES.flatMap((size) => OUTPUT_FPS.map((fps) => ({
    ...size, fps, key: outputKey(size.width, size.height, fps), label: formatRenderLabel(size.width, size.height, fps),
  }))).concat([
    { width: 1080, height: 1080, fps: 60, key: "1080x1080@60", label: "1080x1080 @ 60fps" },
    { width: 1080, height: 1920, fps: 60, key: "1080x1920@60", label: "1080x1920 @ 60fps" },
  ]), []);

  const value = useMemo(() => ({
    settings,
    sourceFormat: input,
    sourceFormatKey: makeSourceFormatKey(input.width, input.height, input.fps),
    sourceFormatOptions,
    output,
    outputKey: output.mode === MATCH_INPUT ? MATCH_INPUT : outputKey(output.width, output.height, output.fps),
    outputOptions,
    virtualCameraKey: outputKey(
      settings?.virtualCameraWidth || output.width,
      settings?.virtualCameraHeight || output.height,
      settings?.virtualCameraFps || output.fps,
    ),
    settingsCollapsed: Boolean(settings?.settingsCollapsed),
    cameraMode: settings?.cameraMode || "off",
    cameraTransition,
    status,
    coreVersion: renderer.coreVersion || runtimeStatus.hostVersion || "cloud",
    frameCount: displayedRenderTelemetry.frameCount,
    source: renderer.source,
    activeLenses: renderer.activeLenses || [],
    lensEnabled: Boolean((renderer.activeLenses || []).length),
    lastLens,
    lensTransition,
    cameras, cachedLenses, cachedLensInfo, apiCalls, logLines, lastError: effectiveLastError,
    favorites, favoriteIds: new Set(favorites.map((lens) => lens.lensId)), recentLenses, favoriteError, busyLensId,
    debugOverlayVisible,
    initialized: renderer.ready,
    smokeMode,
    telemetryLogText: runtimeStatus.available ? "Native runtime connected" : "Native runtime unavailable",
    virtualCameraStatusText,
    renderFps: displayedRenderTelemetry.fps,
    renderFpsSource: displayedRenderTelemetry.source,
    matchInputOutputKey: MATCH_INPUT,
    previewShellRef,
    previewUrl,
    runtimeStatus,
    utilityPanel,
    sessionLogInfo,
    actions: {
      attachPreviewFrame, enumerateCameras, selectCamera, setCameraMode, startCamera, startCameraByLabel, stopCamera, applyOutputResolution,
      applySourceFormat, refreshCachedLenses, loadCachedLens, loadLensUrl, loadLensFromFile,
      loadLensFromSelector, removeLens, clearLenses, setDebugOverlay, saveApiSettings,
      copyApiToken, rotateApiToken,
      setLensEnabled,
      saveVirtualCameraSettings, saveRendererSettings, runVirtualCamera, toggleSettingsCollapsed,
      saveRemoteControlSettings,
      refreshFavorites, toggleFavorite, selectCatalogLens, toggleCatalogLens,
      openUtilityPanel, closeUtilityPanel,
      openSessionLog,
      reportApiCalls: async () => setApiCalls(await host.getApiCalls()), setError,
    },
  }), [
    apiCalls, applyOutputResolution, applySourceFormat, attachPreviewFrame, cachedLensInfo, cachedLenses, closeUtilityPanel, copyApiToken,
    busyLensId, cameraTransition, cameras, clearLenses, debugOverlayVisible, displayedRenderTelemetry, effectiveLastError, enumerateCameras, favoriteError, favorites, input, lastLens, lensTransition, loadCachedLens,
    loadLensFromFile, loadLensFromSelector, loadLensUrl, logLines, output, outputOptions, previewUrl,
    openSessionLog, openUtilityPanel, recentLenses, refreshCachedLenses, refreshFavorites, removeLens, renderFps, renderer, runVirtualCamera, runtimeStatus,
    rotateApiToken, saveApiSettings, saveRemoteControlSettings, saveRendererSettings, saveVirtualCameraSettings, setDebugOverlay, setError, settings, smokeMode,
    selectCamera, selectCatalogLens, setCameraMode, setLensEnabled, sourceFormatOptions, startCamera, startCameraByLabel, status, stopCamera, toggleCatalogLens, toggleFavorite,
    sessionLogInfo, toggleSettingsCollapsed, utilityPanel, virtualCameraStatusText,
  ]);

  return <LensAppContext.Provider value={value}>{children}</LensAppContext.Provider>;
}
